import { useState } from "react";
import Icon from "./Icon";
import { createExpense, updateExpense, deleteExpense } from "../api/paymentsService";
import { formatDate, formatINRExact } from "../utils/formatters";
import { showApiError, showToast } from "../utils/toast";

// Expenses on a bill, shown in both the payment dialog and the task dialog.
// `instance.expected_amount` already includes the expenses total, so the base
// fee is derived (total − expenses). After any change the backend returns the
// fresh instance; we hand it back via onChanged so the parent re-renders.
//
// Props:
//   instance   — payment instance with an `expenses` array + expected_amount
//   canManage  — show add/edit/delete affordances
//   onChanged  — (freshInstance) => void, called after a successful mutation
export default function PaymentExpensesSection({ instance, canManage = false, onChanged }) {
  const [editing, setEditing] = useState(null); // null | "new" | <expense id>

  const expenses = Array.isArray(instance?.expenses) ? instance.expenses : [];
  const expensesTotal = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const total = Number(instance?.expected_amount || 0);
  const base = total - expensesTotal;
  const isCancelled = instance?.status === "cancelled";

  const remove = async (expense) => {
    if (!window.confirm(`Remove the ${formatINRExact(expense.amount)} expense "${expense.description}"? The bill total drops by this amount.`)) return;
    try {
      const res = await deleteExpense(expense.id);
      showToast("success", "Expense removed");
      onChanged?.(res?.data?.instance);
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <div className="card-title">Expenses</div>
        <span className="text-xs t-muted">
          {expenses.length} item{expenses.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="card-body col gap-sm">
        {/* Division: base fee + expenses = total */}
        <div className="row gap-md" style={{ flexWrap: "wrap" }}>
          <Stat label="Base fee" value={formatINRExact(base)} />
          <Stat label="Expenses" value={formatINRExact(expensesTotal)} />
          <Stat label="Total expected" value={formatINRExact(total)} highlight />
        </div>

        {expenses.length === 0 && editing !== "new" && (
          <div className="text-xs t-muted" style={{ padding: "4px 0" }}>No expenses added.</div>
        )}

        {expenses.length > 0 && (
          <div className="col gap-sm">
            {expenses.map((e) => editing === e.id ? (
              <ExpenseForm
                key={e.id}
                mode="edit"
                expense={e}
                onCancel={() => setEditing(null)}
                onSaved={(inst) => { setEditing(null); onChanged?.(inst); }}
              />
            ) : (
              <ExpenseRow
                key={e.id}
                expense={e}
                canManage={canManage}
                onEdit={() => setEditing(e.id)}
                onDelete={() => remove(e)}
              />
            ))}
          </div>
        )}

        {canManage && !isCancelled && (
          editing === "new" ? (
            <ExpenseForm
              mode="create"
              instanceId={instance.id}
              onCancel={() => setEditing(null)}
              onSaved={(inst) => { setEditing(null); onChanged?.(inst); }}
            />
          ) : (
            <div>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing("new")}>
                <Icon name="plus" size={12} /> Add expense
              </button>
            </div>
          )
        )}

        {isCancelled && canManage && (
          <div className="text-xs" style={{ color: "var(--warning)" }}>
            This bill is cancelled. Un-cancel it before adding expenses.
          </div>
        )}
      </div>
    </div>
  );
}

function ExpenseRow({ expense, canManage, onEdit, onDelete }) {
  return (
    <div
      className="row"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 6,
        padding: "8px 12px",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row gap-sm" style={{ alignItems: "baseline" }}>
          <span className="t-primary fw-600">{formatINRExact(expense.amount)}</span>
          <span className="t-muted text-xs" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {expense.description}
          </span>
        </div>
        <div className="text-xs t-muted" style={{ marginTop: 2 }}>
          {expense.incurred_on ? formatDate(expense.incurred_on) : "—"}
          {expense.recorder?.name ? ` · by ${expense.recorder.name}` : ""}
        </div>
      </div>
      {canManage && (
        <>
          <button className="icon-btn" title="Edit" onClick={onEdit}><Icon name="edit" size={13} /></button>
          <button className="icon-btn" title="Remove" style={{ color: "var(--danger)" }} onClick={onDelete}><Icon name="trash" size={13} /></button>
        </>
      )}
    </div>
  );
}

function ExpenseForm({ mode, instanceId, expense, onCancel, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState(() => ({
    amount: expense?.amount ?? "",
    description: expense?.description ?? "",
    incurred_on: expense?.incurred_on ? String(expense.incurred_on).slice(0, 10) : today,
  }));
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        amount: Number(form.amount),
        description: form.description.trim(),
        incurred_on: form.incurred_on || null,
      };
      const res = mode === "edit"
        ? await updateExpense(expense.id, payload)
        : await createExpense(instanceId, payload);
      showToast("success", mode === "edit" ? "Expense updated" : "Expense added");
      onSaved?.(res?.data?.instance);
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const valid = Number(form.amount) > 0 && form.description.trim() !== "";

  return (
    <div className="card" style={{ background: "var(--primary-50)", borderColor: "#bfdbfe" }}>
      <div className="card-body col gap-sm">
        <div className="row gap-md" style={{ flexWrap: "wrap" }}>
          <div className="field" style={{ flex: 1, minWidth: 120 }}>
            <label className="field-label">Amount (₹)</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              className="input"
              autoFocus
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
          </div>
          <div className="field" style={{ flex: 1, minWidth: 140 }}>
            <label className="field-label">Date</label>
            <input
              type="date"
              className="input"
              value={form.incurred_on}
              onChange={(e) => setForm({ ...form, incurred_on: e.target.value })}
            />
          </div>
        </div>
        <div className="field">
          <label className="field-label">Description</label>
          <input
            type="text"
            className="input"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="e.g. Govt filing fee, courier, stamp duty"
          />
        </div>
        <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
          <button className="btn btn-secondary btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={submit} disabled={saving || !valid}>
            <Icon name="check" size={12} /> {saving ? "Saving…" : mode === "edit" ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div style={{ minWidth: 110 }}>
      <div className="text-xs t-muted fw-600" style={{ letterSpacing: 0.4, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2, color: highlight ? "var(--ink-900)" : "var(--ink-700)" }}>{value}</div>
    </div>
  );
}
